import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { PURGE } from "redux-persist";

// Asynchronous thunk to handle login
export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password, impersonate = null }, { rejectWithValue }) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    try {
      const response = await axios.post(`${baseUrl}/Token/V2`, {
        grant_Type: "password",
        user_Name: username,
        impersonate_ID: impersonate,
        password,
        route_ID: 1,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue({ error: err.response?.data?.message || "Invalid username or password"});
    }
  }
);

export const requestPasswordReset = createAsyncThunk(
  "auth/password-reset",
  async ({ emailAddress }, { rejectWithValue }) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    try {
      const response = await axios.post(`${baseUrl}/v1/user/requestPasswordReset`, {
        emailAddress,
        entity_ID: 0,
        baseURL: window.location.href,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error resetting password");
    }
  }
);

export const refresh = createAsyncThunk(
  "auth/refresh",
  async (_, { getState, rejectWithValue }) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    try {
      const response = await axios.post(`${baseUrl}/Token/V2`, {
        grant_Type: "refresh",
        user_name: getState().auth.user.refresh_token,
      });
      return response.data; // Return user data on success
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Refresh Token failed"
      );
    }
  }
);

export const getUserInformation = createAsyncThunk(
  "auth/getUserInformation",
  async (_, { getState, rejectWithValue }) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const token = getState().auth.token;

    try {
      const response = await axios.get(
        `${baseUrl}/v1/Auction/getUserInformation`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data; // Return user info on success
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Fetching user information failed"
      );
    }
  }
);

const loginSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    isAuthenticated: false,
    error: null,
    token: null,
    userProfile: null,
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    loading : false,
    loginLoading: false
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.token = null;
      localStorage.clear();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.loginLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.token = action.payload.token;
        state.user = action.payload;
        if ((state.token = action.payload.token)) {
          state.isAuthenticated = true;
        }
        state.error = null;
        state.loginLoading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.loginLoading = false;
      })
      .addCase(refresh.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(refresh.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.token = action.payload.token;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(refresh.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
      })
      .addCase(getUserInformation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUserInformation.fulfilled, (state, action) => {
        state.userProfile = action.payload.userProfile; // Merge new user data
      })
      .addCase(getUserInformation.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { logout } = loginSlice.actions;
export default loginSlice.reducer;

export const clearPersistedStorage = () => ({
  type: PURGE,
  key: "root",
  result: () => null,
});
